import React from 'react';

import type { TxsSocketType } from './socket/types';
import type { Transaction, TransactionsSortingField, TransactionsSortingValue } from 'types/api/transaction';

import config from 'configs/app';
import { AddressHighlightProvider } from 'lib/contexts/addressHighlight';
import useInitialList from 'lib/hooks/useInitialList';
import useLazyRenderedList from 'lib/hooks/useLazyRenderedList';
import { currencyUnits } from 'lib/units';
import { TableBody, TableColumnHeader, TableColumnHeaderSortable, TableHeaderSticky, TableRoot, TableRow } from 'toolkit/chakra/table';
import TimeFormatToggle from 'ui/shared/time/TimeFormatToggle';

import TxsSocketNotice from './socket/TxsSocketNotice';
import TxsTableItem from './TxsTableItem';

type Props = {
  txs: Array<Transaction>;
  sort: TransactionsSortingValue;
  onSortToggle: (field: TransactionsSortingField) => void;
  top: number;
  showBlockInfo: boolean;
  socketType?: TxsSocketType;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  mainPageTable?: boolean;
};

const TxsTable = ({
  txs,
  sort,
  onSortToggle,
  top,
  showBlockInfo,
  socketType,
  currentAddress,
  enableTimeIncrement,
  isLoading,
  mainPageTable,
}: Props) => {
  const { cutRef, renderedItemsNum } = useLazyRenderedList(txs, !isLoading);
  const initialList = useInitialList({
    data: txs ?? [],
    idFn: (item) => item.hash,
    enabled: !isLoading,
  });

  const feeCurrency = config.UI.views.tx.hiddenFields?.fee_currency || config.chain.hasMultipleGasCurrencies ?
    '' :
    ' ' + currencyUnits.ether;

  return (
    <AddressHighlightProvider>
      <TableRoot minWidth={ mainPageTable ? undefined : '950px' } w="100%" tableLayout={ mainPageTable ? 'fixed' : undefined }>
        <TableHeaderSticky top={ top } backgroundColor={ mainPageTable ? 'blue.50' : undefined }>
          { mainPageTable ? (
            <TableRow>
              { showBlockInfo ? (
                <TableColumnHeaderSortable
                  width="10%"
                  sortField="block_number"
                  sortValue={ sort }
                  onSortToggle={ onSortToggle }
                  py={ 5 }
                  fontWeight={ 700 }
                >
                  شماره بلاک
                </TableColumnHeaderSortable>
              ) : <TableColumnHeader width="10%" py={ 5 } fontWeight={ 700 }>شماره بلاک</TableColumnHeader> }
              <TableColumnHeader width="23%" py={ 5 } fontWeight={ 700 }>هش تراکنش</TableColumnHeader>
              <TableColumnHeader width="18%" py={ 5 } fontWeight={ 700 }>از آدرس</TableColumnHeader>
              <TableColumnHeader width="18%" py={ 5 } fontWeight={ 700 }>به آدرس</TableColumnHeader>
              <TableColumnHeaderSortable
                width="14%"
                isNumeric
                sortField="value"
                sortValue={ sort }
                onSortToggle={ onSortToggle }
                py={ 5 }
                fontWeight={ 700 }
              >
                { `مقدار ${ currencyUnits.ether }` }
              </TableColumnHeaderSortable>
              <TableColumnHeader width="17%" py={ 5 } fontWeight={ 700 }>نوع و وضعیت</TableColumnHeader>
            </TableRow>
          ) : (
            <TableRow>
              <TableColumnHeader width="54px"></TableColumnHeader>
              <TableColumnHeader width="180px">
                هش تراکنش
                <TimeFormatToggle/>
              </TableColumnHeader>
              <TableColumnHeader width="160px">نوع</TableColumnHeader>
              <TableColumnHeader width="20%">متد</TableColumnHeader>
              { showBlockInfo && (
                <TableColumnHeaderSortable
                  width="18%"
                  sortField="block_number"
                  sortValue={ sort }
                  onSortToggle={ onSortToggle }
                >
                  بلاک
                </TableColumnHeaderSortable>
              ) }
              <TableColumnHeader width="224px">از/به</TableColumnHeader>
              { !config.UI.views.tx.hiddenFields?.value && (
                <TableColumnHeaderSortable
                  width="20%"
                  isNumeric
                  sortField="value"
                  sortValue={ sort }
                  onSortToggle={ onSortToggle }
                >
                  { `مقدار ${ currencyUnits.ether }` }
                </TableColumnHeaderSortable>
              ) }
              { !config.UI.views.tx.hiddenFields?.tx_fee && (
                <TableColumnHeaderSortable
                  width="20%"
                  isNumeric
                  pr={ 5 }
                  sortField="fee"
                  sortValue={ sort }
                  onSortToggle={ onSortToggle }
                >
                  { `کارمزد${ feeCurrency }` }
                </TableColumnHeaderSortable>
              ) }
            </TableRow>
          ) }
        </TableHeaderSticky>
        <TableBody>
          { socketType && <TxsSocketNotice type={ socketType } place="table" isLoading={ isLoading }/> }
          { txs.slice(0, renderedItemsNum).map((item, index) => (
            <TxsTableItem
              key={ item.hash + (isLoading ? index : '') }
              tx={ item }
              showBlockInfo={ showBlockInfo }
              currentAddress={ currentAddress }
              enableTimeIncrement={ enableTimeIncrement }
              isLoading={ isLoading }
              animation={ initialList.getAnimationProp(item) }
              mainPageTable={ mainPageTable }
            />
          )) }
        </TableBody>
      </TableRoot>
      <div ref={ cutRef }/>
    </AddressHighlightProvider>
  );
};

export default React.memo(TxsTable);
